import { queryOptions } from "@tanstack/react-query";
import { fetchAutoPay } from "@features/membership/api/autoPay";
import { fetchEligiblePlans } from "@features/membership/api/eligiblePlans";
import { fetchMembership } from "@features/membership/api/membership";
import { fetchPaymentHistory } from "@features/membership/api/paymentHistory";
import { fetchPaymentMethods } from "@features/membership/api/paymentMethods";
import { fetchUpgradeCalculation } from "@features/membership/api/upgrade";
import type { MembershipPlanType } from "@features/membership/model/types";

/**
 * membership 도메인 쿼리 팩토리.
 * 무효화 시 기존 API 주석과 동일한 키 사용: ["membership"], ["paymentMethods"], ["autoPay"] 등.
 */
export const membershipQueries = {
  all: () => ["membership"] as const,
  membershipKey: () => [...membershipQueries.all(), "membership-info"] as const,
  eligiblePlansKey: () => [...membershipQueries.all(), "eligiblePlans"] as const,
  upgradeCalculationKey: (type: MembershipPlanType) =>
    [...membershipQueries.all(), "upgradeCalculation", type] as const,
  paymentMethodsKey: () => [...membershipQueries.all(), "paymentMethods"] as const,
  paymentHistoryKey: (page: number) => [...membershipQueries.all(), "paymentHistory", page] as const,
  autoPayKey: () => [...membershipQueries.all(), "autoPay"] as const,

  membership: () =>
    queryOptions({
      queryKey: membershipQueries.membershipKey(),
      queryFn: fetchMembership,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),

  eligiblePlans: () =>
    queryOptions({
      queryKey: membershipQueries.eligiblePlansKey(),
      queryFn: fetchEligiblePlans,
    }),

  upgradeCalculation: (type: MembershipPlanType) =>
    queryOptions({
      queryKey: membershipQueries.upgradeCalculationKey(type),
      queryFn: () => fetchUpgradeCalculation(type),
    }),

  paymentMethods: () =>
    queryOptions({
      queryKey: membershipQueries.paymentMethodsKey(),
      queryFn: fetchPaymentMethods,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),

  paymentHistory: (page: number, pageSize = 5) =>
    queryOptions({
      queryKey: membershipQueries.paymentHistoryKey(page),
      queryFn: () => fetchPaymentHistory(page, pageSize),
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),

  autoPay: () =>
    queryOptions({
      queryKey: membershipQueries.autoPayKey(),
      queryFn: fetchAutoPay,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),
};
